import { formatDate } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { AnnouncementModel } from '@app/core/model/announcement.model';
import { LibraryDocModel } from '@app/core/model/library-doc.model';
import { UserModel } from '@app/core/model/user.model';
import { AnnouncementListService } from '@app/core/service/announcement-list.service';
import { LibraryApiService } from '@app/core/service/api/library.api.service';
import { EnvironmentService } from '@app/core/service/environment.service';
import { ToastService } from '@app/core/service/toast.service';
import { AnnouncementRespondModalComponent } from '@app/modals/announcement-respond-modal/announcement-respond-modal.component';
import { SurveyJsViewerComponent } from '@app/modules/surveyjs-viewer/surveyjs-viewer.component';
import { isMobile } from '@app/utils/is-mobile';
import { showModal, showWysiwygModal } from '@app/utils/modal';
import { formatUploadedBody } from '@app/utils/wysiwyg-editor';
import {
  EAnnouncementAcknowledgeButtonText,
  EAnnouncementActions,
  EAnnouncementRespondButtonText,
  EAnnouncementType
} from '@backend/models/types/announcement';
import { LoadingController, ModalController } from '@ionic/angular';

@Component({
  selector: 'app-announcement-preview',
  templateUrl: './announcement-preview.component.html',
  styleUrls: ['./announcement-preview.component.scss'],
  standalone: false
})
export class AnnouncementPreviewComponent implements OnChanges {
  @Input()
  public readonly announcement: AnnouncementModel = null;

  @Input()
  public readonly announcementIndex: number = -1;

  @Output()
  public cancelClicked = new EventEmitter<void>();

  @Output()
  public announcementAcknowledged = new EventEmitter<{
    index: number;
    id: string;
  }>();

  @Output()
  public announcementResponded = new EventEmitter<{
    index: number;
    id: string;
  }>();

  @ViewChild('surveyjsViewer')
  public surveyjsViewer!: SurveyJsViewerComponent;

  protected readonly announcementActions = EAnnouncementActions;
  protected readonly announcementAcknowledgeButtonText =
    EAnnouncementAcknowledgeButtonText;
  protected readonly announcementRespondButtonText =
    EAnnouncementRespondButtonText;
  protected readonly announcementType = EAnnouncementType;

  protected isM = false;
  protected serverUrl = '';
  protected body = '';
  protected thread: AnnouncementModel[] = [];
  protected user: UserModel | null;

  public constructor(
    private readonly _toastService: ToastService,
    private readonly _loadingCtrl: LoadingController,
    private readonly _announcementListService: AnnouncementListService,
    private readonly _environmentService: EnvironmentService,
    private readonly _modalCtrl: ModalController,
    private readonly _authenticationService: AuthenticationService,
    private readonly _libraryApiService: LibraryApiService
  ) {
    this.isM = isMobile();
    this.serverUrl = this._environmentService.serverUrl;
    this.user = this._authenticationService.user;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (
      changes &&
      changes.announcementIndex.currentValue !== -1 &&
      changes.announcement.currentValue &&
      changes.announcement.currentValue._id
    ) {
      this.body = formatUploadedBody(
        changes.announcement.currentValue.body,
        changes.announcement.currentValue.attachments,
        this.serverUrl
      );

      this._announcementListService
        .readAnnouncement(changes.announcement.currentValue._id)
        .subscribe();

      this._announcementListService
        .getThreadOfAnnouncement(changes.announcement.currentValue._id)
        .subscribe((thread) => {
          this.thread = thread;
        });
    }
  }

  protected get editedString() {
    return this.announcement && this.announcement.edited
      ? `Last edit: ${this.fDateAndTime(this.announcement.edited)}`
      : '';
  }

  protected onCancelClicked() {
    this.cancelClicked.emit();
  }

  protected fDateAndTime(date: Date) {
    return formatDate(date, 'MMM dd, yyyy h:mm a', 'en-US');
  }

  protected viewLibraryDocument() {
    if (!this.announcement.libraryDoc) {
      return;
    }

    const loading = this._loadingCtrl.create({
      message: 'Loading...'
    });

    loading.then((loader) => {
      loader.present();

      this._libraryApiService
        .getLibraryDoc({ path: { libraryDocId: this.announcement.libraryDoc } })
        .subscribe({
          next: (libraryDoc: LibraryDocModel) => {
            loader.dismiss();

            const formattedBody = formatUploadedBody(
              libraryDoc.body,
              libraryDoc.attachments,
              this._environmentService.serverUrl
            );

            showWysiwygModal(
              {
                title: libraryDoc.title,
                body: formattedBody,
                mode: 'view',
                cancelBtnText: 'Close',
                dismissable: true
              },
              this._modalCtrl
            );
          },
          error: () => {
            loader.dismiss();
            this._toastService.presentToast('Unable to load document');
          }
        });
    });
  }

  protected async acknowledge() {
    if (this.announcement.announcementType === EAnnouncementType.SURVEY) {
      this.tryCompleteSurvey();
    } else {
      const loading = await this._loadingCtrl.create({
        message: 'Loading...'
      });
      loading.present();

      this._announcementListService
        .acknowledgeAnnouncement(this.announcement._id, {})
        .subscribe(() => {
          loading.dismiss();
          this._toastService.presentToast(
            'Announcement acknowledged successfully!'
          );
          this.announcementAcknowledged.emit({
            index: this.announcementIndex,
            id: this.announcement._id
          });
          this.onCancelClicked();
        });
    }
  }

  protected async respond() {
    const { role } = await showModal(
      {
        component: AnnouncementRespondModalComponent,
        props: {
          announcement: this.announcement
        },
        modalName: 'AnnouncementRespondModalComponent'
      },
      this._modalCtrl
    );

    if (role === 'confirm') {
      this.announcementResponded.emit({
        index: this.announcementIndex,
        id: this.announcement._id
      });
      this.onCancelClicked();
    }
  }

  protected tryCompleteSurvey() {
    this.surveyjsViewer.tryComplete();
  }

  protected readonly completeSurvey = async (json: string): Promise<void> => {
    const loading = await this._loadingCtrl.create({
      message: 'Loading...'
    });
    loading.present();

    this._announcementListService
      .acknowledgeAnnouncement(this.announcement._id, { surveyAnswer: json })
      .subscribe(() => {
        loading.dismiss();
        this._toastService.presentToast('Survey completed successfully!');
        this.announcementAcknowledged.emit({
          index: this.announcementIndex,
          id: this.announcement._id
        });
        this.onCancelClicked();
      });
  };
}
